/**
 * A **partial column update** — the second wave, and what carries the first one
 * across a refresh.
 *
 * The screener is delivered in two waves and they run on their own clocks. Wave
 * 1 is everything `qf-us` serves straight from the bars and the symbol table,
 * and it refreshes on its own cadence; wave 2 is the computed columns, pushed by
 * whoever computes them, whenever they are ready. Neither waits for the other.
 * `docs/plans/screener-fast-path.md` is the plan; `getRowId` and
 * `applyTransactionAsync` in `ScreenerGrid` are what make a push land without
 * destroying selection, scroll or focus, so this file only has to produce the
 * merged rows.
 *
 * ## The one rule everything here follows
 *
 * **A push that does not carry a column must not clear that column.** A merge
 * touches only what it carries; every other column — and every row the push has
 * nothing to say about — keeps exactly the value it already had. A screener that
 * blanked its computed columns on every price refresh, while something
 * recomputed them, would be unusable, and the price columns already behave this
 * way: a refresh replaces values, it never empties the grid first.
 *
 * That is why wave 1 arrives here too. A fresh wave-1 snapshot carries none of
 * the computed columns, so applying it as a replacement *is* a push that clears
 * them. Instead the computed columns already on screen are lifted out of the
 * held frames with {@link columnWaveFrom} and merged onto the new snapshot —
 * same primitive, the other direction.
 *
 * ## The skew is deliberate
 *
 * Because the two waves are from different moments, a computed column can be up
 * to one compute cycle behind the price columns beside it: a row may briefly
 * show a `close` that does not agree with a signal derived from a slightly older
 * `close`. **That is accepted.** Do not "fix" it by blanking the computed column
 * on each tick, and do not add a staleness spinner or a grey-out — the value on
 * screen is a real value that was true one cycle ago, and hiding it costs the
 * user the column for the sake of a discrepancy in the last digit.
 *
 * Pure, so all of it is testable without mounting a grid — which is the only way
 * the cases that matter here can be stated at all: they are about what a merge
 * leaves *alone*.
 */

import { COLUMNS_FRAME } from "@qufolio/transport";
import type { ScreenerField, ScreenerFrame, ScreenerRow } from "@qufolio/transport";

import { resolveRowIdField, rowId } from "./row-diff.js";

/** One wave: some columns, for some rows, of one named frame. */
export interface ScreenerColumnWave {
  /** Which frame these columns belong to — `detail`, or a rollup. */
  frame: string;
  /** The identity column every row carries, so rows can be matched up. */
  idField: string;
  /**
   * The columns this wave carries, beside the id. **Empty means nothing to
   * merge**, and the merge then returns the frames it was given by identity —
   * which is what makes this shippable against a single-wave payload at zero
   * cost.
   */
  fields: readonly ScreenerField[];
  /** One row per symbol: the id, plus whichever of `fields` it has a value for. */
  rows: readonly ScreenerRow[];
}

/**
 * Lift some columns out of the frames already held, as a wave.
 *
 * Used to carry computed columns across a wave-1 refresh — see the header. A
 * field the frame does not have is skipped rather than carried as null, and a
 * row with no value for a carried field contributes nothing for it, so this
 * cannot clear anything on the far side of the merge either.
 *
 * The id field is resolved rather than passed, because `resolveRowIdField` is
 * already the one answer the grid uses and a second opinion about which column
 * identifies a row would merge the wrong rows together in silence.
 */
export function columnWaveFrom(
  frames: readonly ScreenerFrame[],
  frameName: string,
  fields: readonly string[],
  /**
   * Carry a null as a null. Off for lifting held columns across a refresh,
   * where a missing value must clear nothing; on for an incoming push, whose
   * null is an answer — a Recs row that stopped being a recommendation.
   */
  keepNulls = false,
): ScreenerColumnWave {
  const frame = frames.find((f) => f.name === frameName);
  const idField = resolveRowIdField(frame?.fields ?? []);
  const wanted = new Set(fields);
  const carried = (frame?.fields ?? []).filter((f) => wanted.has(f.name));

  if (!frame || !idField || carried.length === 0) {
    return { frame: frameName, idField: idField ?? "", fields: [], rows: [] };
  }

  const names = carried.map((f) => f.name);
  const rows = frame.rows.map((row) => {
    const out: ScreenerRow = { [idField]: row[idField] ?? null };
    for (const name of names) {
      const value = row[name];
      if (value != null) out[name] = value;
      else if (keepNulls && name in row) out[name] = null;
    }
    return out;
  });

  return { frame: frameName, idField, fields: carried, rows };
}

/**
 * Merge a wave into the frames already held, returning the new frame list.
 *
 * Additive in both directions: a column the wave introduces is appended to the
 * frame's schema — never inserted, so nothing on screen shifts — and a column
 * the wave does not name is left exactly as it was. Rows keep their identity
 * when nothing was written to them, so the grid's own diff sees an update only
 * where there is one.
 */
export function mergeColumnWave(
  frames: readonly ScreenerFrame[],
  wave: ScreenerColumnWave,
): readonly ScreenerFrame[] {
  // Nothing to say, so nothing changes — by identity, which is what keeps a
  // single-wave payload byte-for-byte the object the decoder produced.
  if (wave.fields.length === 0 || !wave.idField) return frames;

  const index = frames.findIndex((f) => f.name === wave.frame);
  if (index < 0) return frames;
  const target = frames[index]!;

  const byId = new Map<string, ScreenerRow>();
  for (const row of wave.rows) {
    const id = rowId(row, wave.idField);
    if (id) byId.set(id, row);
  }

  const names = wave.fields.map((f) => f.name);
  const rows = target.rows.map((row) => {
    const carried = byId.get(rowId(row, wave.idField));
    // **Not in this push, so not touched.** Not nulled: a roster row the wave
    // had nothing to say about is a row whose value is simply not new, and
    // clearing it would blank the column for whoever is looking at it.
    if (!carried) return row;

    let next: ScreenerRow | undefined;
    for (const name of names) {
      if (!(name in carried)) continue;
      next ??= { ...row };
      next[name] = carried[name] ?? null;
    }
    return next ?? row;
  });

  const present = new Set(target.fields.map((f) => f.name));
  const fields = [...target.fields, ...wave.fields.filter((f) => !present.has(f.name))];

  const out = [...frames];
  out[index] = { ...target, fields, rows };
  return out;
}

// ── the `push` frame: what this payload speaks for ──────────────────────────

/** The frame's name on the wire. Contract, shared with the server. */
export const PUSH_FRAME = "push";

/** Wave 1 is the roster and everything served straight from `qf-us`. */
const WAVE_ONE = 1;

/** What one payload declared it carries. */
export interface ScreenerPush {
  /** Every column this payload has a value for, in the order it listed them. */
  fields: string[];
  /** Which wave each carried column came from. */
  waveOf: Map<string, number>;
  /**
   * The server's monotonic stamp per carried column.
   *
   * Nothing acts on it yet. Carried because it is what lets a client drop a push
   * that arrived out of order — and a wire field that is decoded but unread is
   * far cheaper than one that has to be renegotiated to get back.
   */
  generationOf: Map<string, number>;
  /**
   * Whether this payload carries wave 1, and therefore **the roster itself**.
   *
   * The one bit that decides which side of the merge is the base: a payload that
   * carries the roster defines which rows exist, so it is the base and the
   * columns it does not mention are carried onto it. A wave-2-only push has no
   * roster and no authority over the row set, so what is already on screen stays
   * the base and only the named columns are written into it.
   */
  carriesRoster: boolean;
}

/**
 * Read the `push` frame, or `undefined` when the payload has none.
 *
 * **Undefined means "everything", not "nothing".** A server that does not
 * declare what it pushed is declaring the whole payload — which is exactly the
 * single-response `/sym/…/screen` route this feature has to keep working
 * against — so the caller replaces rather than merges. Reading absence as an
 * empty push would merge nothing into the frames already held and the screen
 * would never update at all.
 */
export function readScreenerPush(
  frames: readonly ScreenerFrame[] | undefined,
): ScreenerPush | undefined {
  const frame = frames?.find((f) => f.name === PUSH_FRAME);
  if (!frame) return undefined;

  const fields: string[] = [];
  const waveOf = new Map<string, number>();
  const generationOf = new Map<string, number>();
  let carriesRoster = false;

  for (const row of frame.rows) {
    const field = typeof row["field"] === "string" ? row["field"] : "";
    if (!field || waveOf.has(field)) continue;
    const wave = typeof row["wave"] === "number" ? row["wave"] : WAVE_ONE;
    const generation = typeof row["generation"] === "number" ? row["generation"] : 0;
    fields.push(field);
    waveOf.set(field, wave);
    generationOf.set(field, generation);
    if (wave === WAVE_ONE) carriesRoster = true;
  }

  return { fields, waveOf, generationOf, carriesRoster };
}

/**
 * Fold one arriving payload into the frames already on screen.
 *
 * The whole two-wave client, in one pure function. Three cases, and each is a
 * different answer to *which side owns the row set*:
 *
 * 1. **Nothing held** — first paint. The payload is what there is, *unless* it
 *    declares itself wave 2 only: a payload with no roster has no authority over
 *    the row set even when there is no row set, and its `detail` frame carries
 *    just the id column and the computed ones. Taking it as the base renders a
 *    finished-looking screen with every stored column gone — measured in the
 *    browser on 2026-09-11, and `routers/scan.py` records the other half of it.
 *    The metadata frames are kept (`columns` is the acknowledgement the pending
 *    state turns on) and the rows are left to whoever owns them.
 * 2. **No `push` frame** — an undeclared payload speaks for everything, so it
 *    replaces. This is today's HTTP response, unchanged.
 * 3. **A push that carries wave 1** — it owns the roster, so it is the base, and
 *    every column it did *not* name is lifted off the held frames onto it. That
 *    is the "must not clear" rule applied to a price refresh: a wave-1 push
 *    carries none of the computed columns, so replacing outright would blank
 *    them on every tick while something recomputed them.
 * 4. **A wave-2-only push** — no roster, so the held frames stay the base and
 *    only the named columns are written in. The *metadata* frames still come
 *    from the payload: `columns` carries the acknowledgement this whole state
 *    model turns on, and `timings` describes the push that just arrived.
 *
 * The skew between 3 and 4 — a computed column up to one cycle behind the price
 * beside it — is accepted; see this file's header before "fixing" it.
 */
export function applyScreenerPush(
  held: readonly ScreenerFrame[] | null | undefined,
  incoming: readonly ScreenerFrame[],
  frameName = "detail",
): readonly ScreenerFrame[] {
  const push = readScreenerPush(incoming);

  if (!held || held.length === 0) {
    // Read the push frame *before* the held check, or `carriesRoster` is
    // ignored in the one state where it decides everything — see case 1.
    // Returned by identity when there is nothing to withhold, which is what
    // keeps a single-wave payload the object the decoder produced.
    if (push && !push.carriesRoster) return incoming.filter((f) => f.name !== frameName);
    return incoming;
  }

  if (!push) return incoming;

  if (push.carriesRoster) {
    const carried = new Set(push.fields);
    const keep = (held.find((f) => f.name === frameName)?.fields ?? [])
      .map((f) => f.name)
      .filter((name) => !carried.has(name));
    return mergeColumnWave(incoming, columnWaveFrom(held, frameName, keep));
  }

  const merged = mergeColumnWave(held, columnWaveFrom(incoming, frameName, push.fields, true));
  const out = [...merged];
  for (const frame of incoming) {
    if (frame.name === frameName) continue;
    const at = out.findIndex((f) => f.name === frame.name);
    if (at < 0) {
      out.push(frame);
      continue;
    }
    // **Two frames are per-row dictionaries and accumulate; the rest replace.**
    //
    // `timings`: a screen's time is now spent in three apps — qf-us for wave 1,
    // qf-scr and qf-scr-calc for wave 2 — and the panel is one list. Replacing
    // the frame makes it show whichever wave pushed last, which is a panel that
    // flickers between two answers and is never the whole story. Rows are keyed
    // by name, so a wave's second push updates its own rows and leaves the
    // other's.
    //
    // Safe only because the server prefixes them: `roster` is a stage in both
    // waves, and merging unprefixed rows would have wave 2's silently overwrite
    // wave 1's. See `routers/scan.py`.
    //
    // `columns`: the same shape of claim, and it is this file's one rule again.
    // **A payload that may not own the rows may not own the dictionary
    // either.** We are in the branch where the push does *not* carry the
    // roster, which is precisely wave 2 — it speaks for its own computed
    // columns and has never heard of `close` or `sector`. Copying its frame
    // over wave 1's discarded every stored column's entry on each push.
    //
    // Nothing on screen went blank, which is why this survived: `columnState`
    // consults the acknowledgement only for a column with no value yet. What it
    // did was flip such a column from *being computed* to *nobody is computing
    // this* — the one distinction `column-source.ts` exists to preserve.
    //
    // That is the whole observable defect today, and the fix is not sized for
    // more. `readColumnSources` is the only reader of the *merged* frame;
    // `decodeColumnMeta` reads the same rows for a header's label, description
    // and timeframe, but it runs at decode time on a raw payload and never sees
    // this output. Keeping those rows intact is still right — it is what stops
    // the next reader of a merged frame inheriting the bug — but no header is
    // fixed by this change.
    //
    // The other direction is deliberately left replacing: a wave-1 payload
    // carries the roster and restates every column it serves, including the
    // computed ones it was asked for, so there is nothing of wave 2's for it to
    // lose that it does not say again itself.
    if (frame.name === TIMINGS_FRAME) out[at] = mergeTimings(out[at]!, frame);
    else if (frame.name === COLUMNS_FRAME) out[at] = mergeColumnAcks(out[at]!, frame);
    else out[at] = frame;
  }
  return out;
}


/** The frame's name on the wire. Shared with `lib/timings.ts` and `perf.py`. */
const TIMINGS_FRAME = "timings";

/**
 * Fold one wave's stage rows into another's, keyed by stage name.
 *
 * `total` is the exception and is taken from the **incoming** payload: it is
 * the bracket around one request, not a sum of the list, and two totals in a
 * panel that already shows an unattributed remainder would make that remainder
 * meaningless.
 */
function mergeTimings(held: ScreenerFrame, incoming: ScreenerFrame): ScreenerFrame {
  const byName = new Map<string, ScreenerRow>();
  for (const row of held.rows) {
    const name = typeof row["name"] === "string" ? row["name"] : "";
    if (name && name !== "total") byName.set(name, row);
  }
  for (const row of incoming.rows) {
    const name = typeof row["name"] === "string" ? row["name"] : "";
    if (name && name !== "total") byName.set(name, row);
  }
  const total = incoming.rows.find((r) => r["name"] === "total")
    ?? held.rows.find((r) => r["name"] === "total");
  return {
    ...incoming,
    rows: total ? [...byName.values(), total] : [...byName.values()],
  };
}

/**
 * Fold one wave's `columns` acknowledgements into another's, keyed by field.
 *
 * `readColumnSources` is the reader that matters here, and it indexes by
 * `field`: the `source` on a column with no value yet is what tells *being
 * computed* from *nobody is computing this*. Replacing the frame dropped every
 * row the incoming payload did not restate, so a wave-2 push answered for
 * columns it had never heard of by saying nothing about them.
 *
 * The rows also carry what `decodeColumnMeta` reads — label, description,
 * timeframe. That function runs at decode time on a raw payload and never sees
 * a merged frame, so it is not what this fixes; the rows are kept whole so a
 * later reader of this output does not inherit the same hole.
 *
 * **Held order is kept and the incoming appends.** Wave 1 owns the roster and
 * therefore the column order; a wave-2 column it has not mentioned goes on the
 * end rather than reshuffling what is on screen.
 *
 * A row with no `field` is dropped — both readers already skip one, since an
 * entry nothing can address is not an acknowledgement of anything.
 *
 * The declared `fields` are unioned rather than taken from either side. A wave
 * that carries fewer columns in its schema than the other still describes real
 * rows, and narrowing the schema to the last push would leave held rows
 * carrying values the frame no longer admits to having.
 */
function mergeColumnAcks(held: ScreenerFrame, incoming: ScreenerFrame): ScreenerFrame {
  const byField = new Map<string, ScreenerRow>();
  for (const row of [...held.rows, ...incoming.rows]) {
    const field = typeof row["field"] === "string" ? row["field"] : "";
    if (field) byField.set(field, row);
  }

  const present = new Set(incoming.fields.map((f) => f.name));
  const fields = [
    ...incoming.fields,
    ...held.fields.filter((f) => !present.has(f.name)),
  ];

  return { ...incoming, fields, rows: [...byField.values()] };
}
